import { autoinject } from 'aurelia-framework';
import { clamp } from 'app/shared/lib/noop';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { CustomAttributeBase } from 'app/shared/attributes/abstracts/custom-attribute-base';
import { animateOnce } from 'app/shared/lib/dom';
import { debounce } from 'app/shared/lib/decorators';

type MovementStrategy = (this: EssScrollbarCustomAttribute, event: MouseEvent) => number;

@autoinject()
export class EssScrollbarCustomAttribute extends CustomAttributeBase {

    protected value: boolean = true;

    protected scrollbar: HTMLElement;
    protected handle: HTMLElement;

    protected scrollbarWidth: number = 0;
    protected handleWidth: number = 0;
    protected maxScrollPosition: number = 0;
    protected scrollDistance: number = 0;
    protected scrollableWidth = 0;
    protected position: number = 0;
    protected anchor: number = 0;
    protected origin: number = -1;

    protected handleMousedown = (event: MouseEvent) => {
        if(event.target === this.scrollbar) {
            const { left, right } = this.handle.getBoundingClientRect();
            if(event.clientX < left)
                this.shiftHandle(-1);
            else if(event.clientX > right) {
                this.shiftHandle(1);
            }
        } else if(event.target === this.handle) {
            this.grabHandle(event.screenX);
        } else {
            return;
        }
        event.preventDefault();
    };

    protected handleMouseup = () => {
        this.releaseHandle();
    };

    protected handleMousemove = (event: MouseEvent) => {
        if(this.origin === -1) {
            return;
        }
        const position = this.strategy.call(this, event);
        this.moveHandle(position);
        event.preventDefault();
    };

    protected resizeObserver: ResizeObserver;
    protected mutationObserver: MutationObserver;
    protected strategy = EssScrollbarCustomAttribute.MOVEMENT_STRATEGIES.get('click-origin')!;

    constructor(element: Element, logger: AppLogger) {
        super(element, logger);
        this.scrollbar = document.createElement('div');
        this.scrollbar.classList.add('ess-scrollbar');
        this.handle = document.createElement('div');
        this.handle.classList.add('ess-scrollbar-handle');
        this.scrollbar.appendChild(this.handle);

        // TODO [Matt] We don't use the .bind pattern often, but there is no
        //  pattern we use today that will fit the case where we want to
        //  debounce a method passed as an argument to an external class. I'd
        //  like to come up with a better solution in the future that allows
        //  us to define a lexically bound and also decorated function-valued
        //  property.
        this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));
        this.mutationObserver = new MutationObserver(this.handleResize.bind(this));
    }

    protected attached() {
        this.valueChanged();
    }

    protected detached() {
        this.disable();
    }

    protected enable() {
        if(!this.element.classList.contains('ess-scrollbar-disabled')) {
            this.resizeObserver.observe(this.element);
            this.mutationObserver.observe(this.element, { childList: true, subtree: true });
            this.element.classList.add('ess-scrollbar-disabled');
            this.element.insertAdjacentElement('afterend', this.scrollbar);
            this.connectEvents();
        }
    }

    protected disable() {
        this.resizeObserver.disconnect();
        this.mutationObserver.disconnect();
        this.element.classList.remove('ess-scrollbar-disabled');
        this.scrollbar.remove();
        this.disconnectEvents();
    }

    protected valueChanged() {
        if(this.value === false) {
            this.disable();
        }
        else {
            this.enable();
        }
    }

    protected connectEvents() {
        document.addEventListener('mouseup', this.handleMouseup);
        document.addEventListener('mousemove', this.handleMousemove);
        this.scrollbar.addEventListener('mousedown', this.handleMousedown);
    }

    protected disconnectEvents() {
        document.removeEventListener('mouseup', this.handleMouseup);
        document.removeEventListener('mousemove', this.handleMousemove);
        this.scrollbar.removeEventListener('mousedown', this.handleMousedown);
    }

    @debounce()
    protected handleResize() {
        const { offsetWidth, scrollWidth } = this.element;
        this.scrollbarWidth = offsetWidth;
        this.scrollableWidth = scrollWidth;
        this.scrollDistance = scrollWidth - offsetWidth;
        this.handleWidth = offsetWidth / scrollWidth;
        this.maxScrollPosition = (1 - this.handleWidth) * this.scrollbarWidth;
        const currentPosition = this.position / this.maxScrollPosition;
        this.scrollbar.style.width = `${this.scrollbarWidth}px`;
        this.handle.style.width = `${100 * this.handleWidth}%`;
        if(!isNaN(currentPosition)) {
            this.moveHandle(currentPosition * this.maxScrollPosition);
        }
        this.scrollbar.classList.toggle('aurelia-hide', this.handleWidth === 1);
    }

    protected grabHandle(origin: number) {
        this.anchor = this.position;
        this.origin = origin;
    }

    protected releaseHandle() {
        this.origin = -1;
    }

    protected moveHandle(position: number) {
        this.position = clamp(position, 0, this.maxScrollPosition);
        this.handle.style.transform = `translateX(${this.position}px)`;
        this.element.scrollLeft = this.position / this.maxScrollPosition * this.scrollDistance;
    }

    protected shiftHandle(delta: number) {
        delta *= this.handleWidth;
        this.position = clamp(this.position + delta * this.scrollbarWidth, 0, this.maxScrollPosition);
        animateOnce(this.handle, { transform: `translateX(${this.position}px)` });
        this.element.scrollBy({ left: delta * this.scrollableWidth, behavior: 'smooth' });
    }

    static MOVEMENT_STRATEGIES = new Map<string, MovementStrategy>([
        ['click-origin', function(this: EssScrollbarCustomAttribute, event: MouseEvent) {
            return this.anchor + event.screenX - this.origin;
        }],
        ['continuous-delta', function(this: EssScrollbarCustomAttribute, event: MouseEvent) {
            return this.position += event.movementX;
        }]
    ]);
}
